import { Inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  ChangeOriginAPI,
  ServicePointSetup,
  ServicePointSetupAPI, SPSBasicResponse
} from '../..';
import { mbeConfigService } from './mbe-config.service';
import { BasicResponse } from '@auth-suite-services/services';

/** See C001 - 2026-02-04 - HE: API may return refSSUID as RefSSUID/refSsuid; normalize so component has it */
function normalizeSpsResponse(raw: Record<string, unknown>): ServicePointSetup {
  const refSSUID = raw['refSSUID'] ?? raw['RefSSUID'] ?? raw['refSsuid'] ?? raw['RefSsuid'];
  return { ...raw, refSSUID } as ServicePointSetup;
}

/** See C002 - 2026-02-04 - HE: Backend expects flat props only; no nested communicationProgramID/fromApplicationProgramID1-5 */
const NESTED_KEYS_TO_STRIP = [
  'communicationProgramID',
  'fromApplicationProgramID1',
  'fromApplicationProgramID2',
  'fromApplicationProgramID3',
  'fromApplicationProgramID4',
  'fromApplicationProgramID5',
];

function flattenSpsPayload(sps: Record<string, unknown>): Record<string, unknown> {
  const out = { ...sps };
  for (const key of NESTED_KEYS_TO_STRIP) {
    const val = out[key];
    if (val != null && typeof val === 'object' && !Array.isArray(val)) {
      const flat = val as Record<string, unknown>;
      const prefix = key;
      for (const k of Object.keys(flat)) {
        const suffix = k.charAt(0).toUpperCase() + k.slice(1);
        out[`${prefix}${suffix}`] = flat[k];
      }
    }
    delete out[key];
  }
  return out;
}

export interface PartnerCredentialsRequest {
  partnerUsername: string;
  uacID: number;
  globalID: number;
}

@Injectable({
  providedIn: 'root',
})
export class ServicePointSetupService {
  private URL;
  private AmassENSURL;
  private MBURL;
  headers: HttpHeaders;
  // added new api route                      14/05/2024 RL
  private shortUrl;

  constructor(
    @Inject(mbeConfigService) private config: any,
    private httpClient: HttpClient
  ) {
    this.AmassENSURL =
      config.REST_SERVER_HOST +
      config.REST_SERVER_url.find(
        (restUrl: { api: string }) => restUrl.api === 'SDSETUP'
      ).url;
    this.URL =
      config.REST_SERVER_HOST +
      config.REST_SERVER_url.find(
        (restUrl: { api: string }) => restUrl.api === 'PADM'
      ).url;
      this.MBURL =
        config.REST_SERVER_HOST +
        config.REST_SERVER_url.find(
          (restUrl: { api: string }) => restUrl.api === 'MBE'
        ).url;
    this.headers = new HttpHeaders().set('Content-Type', 'application/json');
    this.shortUrl = config.REST_SERVER_HOST;
  }

  // tslint:disable-next-line:max-line-length
  getAllSPS(
    pageIndex: number,
    pageSize: number,
    sortActive: string,
    sort: string,
    gidFrom: number,
    gidTo: number,
    creationDate: string,
    servicePointName: string
  ): Observable<ServicePointSetupAPI> {
    console.log('PageIndex; PageSize; SortActive; Sort');
    console.log(pageIndex, pageSize, sortActive, sort);
    const headers = this.headers;
    // tslint:disable-next-line:max-line-length
    return this.httpClient.get<ServicePointSetupAPI>(
      // `${this.URL}/sps/allsps/${pageIndex}/${pageSize}/${
      `${this.URL}/sps/allsps/${pageIndex}/${pageSize}/${
        sortActive ? sortActive : 'id'
      }/${sort ? sort : 'asc'}/${gidFrom ? gidFrom : 0}/${gidTo ? gidTo : 0}/${
        creationDate ? creationDate : '0'
      }/${servicePointName ? servicePointName : '0'}`,
      { headers }
    );
  }

  getSPSSS(id: number): Observable<ServicePointSetup> {
    console.log('id:', id);
    const headers = this.headers;
    // tslint:disable-next-line:max-line-length
    return this.httpClient.get<ServicePointSetup>(
      `${this.URL}/sps/getsps/${id}`,
      { headers }
    );
  }

  getSPS(id: number): Observable<ServicePointSetup> {
    console.log('id:', id);
    const headers = this.headers;
    return this.httpClient
      .get<Record<string, unknown>>(`${this.URL}/sps/getsps/${id}`, {
        headers,
      })
      .pipe(map(normalizeSpsResponse));
  }

  // changed                                              17/04/2024 RL
  updateSPS(sps: any): Observable<SPSBasicResponse> {
    const headers = this.headers;
    let payload = { ...sps } as Record<string, unknown>;
    payload = flattenSpsPayload(payload);
    if ((payload['refSSUID'] == null || payload['refSSUID'] === '') && (payload['id'] != null && payload['id'] !== '')) {
      payload['refSSUID'] = Number(payload['id']);
    } else if (payload['refSSUID'] != null && payload['refSSUID'] !== '') {
      payload['refSSUID'] = Number(payload['refSSUID']);
    }
    console.log('Update SPS: ', JSON.stringify(payload));
    return this.httpClient.post<SPSBasicResponse>(
      // `${this.URL}/mse/updatesps`,
      `${this.MBURL}/mse/updatesps`,
      payload,
      { headers }
    );
  }
  // added                                                  02072026 RL
  updateSPSCX(sps: ServicePointSetup): Observable<BasicResponse> {
    const headers = this.headers;
    console.log('Update SPS: ', JSON.stringify(sps));
    return this.httpClient.post<BasicResponse>(
      // `${this.AmassENSURL}/sps/updatesps`,
      `${this.shortUrl}/api/portaladmin/sps/updatesps`,
      JSON.stringify(sps),
      { headers }
    );
  }

  updateSPSSS(sps: ServicePointSetup): Observable<BasicResponse> {
    const headers = this.headers;
    console.log('Update SPS: ', JSON.stringify(sps));
    return this.httpClient.post<BasicResponse>(
      // `${this.AmassENSURL}/sps/updatesps`,
      `${this.shortUrl}/api/amassapp/sps/updatesps`,
      JSON.stringify(sps),
      { headers }
    );
  }

  changeOrigin(val: ChangeOriginAPI) {
    const headers = this.headers;
    return this.httpClient.post<BasicResponse>(
      `${this.URL}/sps/spsorigin`,
      val,
      { headers }
    );
  }
}

// ========================================
// CHANGE LOG
// ========================================
// C002: PROPERTY DOES NOT EXIST *fromApplicationProgramID4 - backend expects flat props only. What changed: flattenSpsPayload() strips nested keys (communicationProgramID, fromApplicationProgramID1-5). If value is object, flattens to prefix+Key (e.g. fromApplicationProgramID4.programID -> fromApplicationProgramID4ProgramID). Previous behavior: Nested objects sent; %ConvertJSONToObject fails. New behavior: Only flat properties sent. Impact: updatesps succeeds. - 2026-02-04 - HE
